import { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface ShippingCountry {
  id: string;
  country_name: string;
  country_code: string;
  dial_code: string;
  phone_digits: number;
  is_active: boolean;
  display_order?: number | null;
}

export interface CountryPhoneConfig {
  countryCode: string;
  dialCode: string;
  phoneDigits: number;
  placeholder: string;
}

interface ShippingCountriesContextType {
  countries: ShippingCountry[];
  loading: boolean;
  getPhoneConfig: (countryName: string) => CountryPhoneConfig;
  validatePhoneNumber: (phone: string, countryName: string) => { isValid: boolean; error: string };
  formatPhoneWithDialCode: (phone: string, countryName: string) => string;
}

const defaultPhoneConfig: CountryPhoneConfig = {
  countryCode: '',
  dialCode: '+',
  phoneDigits: 10,
  placeholder: '123 456 7890',
};

const generatePlaceholder = (digits: number): string => {
  const patterns: Record<number, string> = {
    8: '1234 5678',
    9: '50 123 4567',
    10: '98765 43210',
  };
  return patterns[digits] || '123 456 7890';
};

const ShippingCountriesContext = createContext<ShippingCountriesContextType | undefined>(undefined);

export const ShippingCountriesProvider = ({ children }: { children: React.ReactNode }) => {
  const [countries, setCountries] = useState<ShippingCountry[]>([]);
  const [loading, setLoading] = useState(true);
  const hasLoadedRef = useRef(false);
  const lastFetchRef = useRef(0);

  const fetchCountries = useCallback(async (opts?: { force?: boolean }) => {
    const now = Date.now();
    if (!opts?.force && now - lastFetchRef.current < 1500) return;
    lastFetchRef.current = now;

    if (!hasLoadedRef.current) setLoading(true);

    try {
      const { data, error } = await supabase
        .from('shipping_countries')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true })
        .order('country_name', { ascending: true });

      if (error) throw error;
      setCountries(data || []);
    } catch (error) {
      console.error('Failed to fetch shipping countries:', error);
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCountries({ force: true });
  }, [fetchCountries]);

  // Keep the website in sync when admin updates country order/settings.
  useEffect(() => {
    const onRefresh = () => fetchCountries();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchCountries();
    };

    window.addEventListener('focus', onRefresh);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('shippingCountriesUpdated', onRefresh as unknown as EventListener);

    return () => {
      window.removeEventListener('focus', onRefresh);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('shippingCountriesUpdated', onRefresh as unknown as EventListener);
    };
  }, [fetchCountries]);

  const getPhoneConfig = (countryName: string): CountryPhoneConfig => {
    const country = countries.find(c => c.country_name === countryName);
    if (!country) return defaultPhoneConfig;

    return {
      countryCode: country.country_code,
      dialCode: country.dial_code,
      phoneDigits: country.phone_digits,
      placeholder: generatePlaceholder(country.phone_digits),
    };
  };

  const validatePhoneNumber = (phone: string, countryName: string): { isValid: boolean; error: string } => {
    const config = getPhoneConfig(countryName);
    const digitsOnly = phone.replace(/\D/g, '');

    if (!digitsOnly) {
      return { isValid: false, error: 'Phone number is required' };
    }

    if (digitsOnly.length !== config.phoneDigits) {
      return {
        isValid: false,
        error: `Phone number must be ${config.phoneDigits} digits for ${countryName}`,
      };
    }

    return { isValid: true, error: '' };
  };

  const formatPhoneWithDialCode = (phone: string, countryName: string): string => {
    const config = getPhoneConfig(countryName);
    const digitsOnly = phone.replace(/\D/g, '');
    return `${config.dialCode} ${digitsOnly}`;
  };

  return (
    <ShippingCountriesContext.Provider
      value={{
        countries,
        loading,
        getPhoneConfig,
        validatePhoneNumber,
        formatPhoneWithDialCode,
      }}
    >
      {children}
    </ShippingCountriesContext.Provider>
  );
};

export const useShippingCountries = () => {
  const context = useContext(ShippingCountriesContext);
  if (!context) {
    throw new Error('useShippingCountries must be used within a ShippingCountriesProvider');
  }
  return context;
};
